/**
 * Relay-style helpers for Core GraphQL (`edges` / `node` / `pageInfo`).
 */

/**
 * @template T
 * @param {{ edges?: Array<{ node?: T | null } | null> } | null | undefined} connection
 * @returns {T[]}
 */
export function transformRelayConnection(connection) {
    return connection?.edges?.map((edge) => edge?.node).filter(Boolean) || [];
}

/**
 * @param {{ pageInfo?: { hasNextPage?: boolean, endCursor?: string | null }, totalCount?: number } | null | undefined} connection
 * @returns {{ hasNextPage: boolean, endCursor: string | null, totalCount: number }}
 */
export function relayPageMeta(connection) {
    const pi = connection?.pageInfo || {};
    const rows = transformRelayConnection(connection);
    return {
        hasNextPage: !!pi.hasNextPage,
        endCursor: pi.endCursor ?? null,
        totalCount:
            typeof connection?.totalCount === "number"
                ? connection.totalCount
                : rows.length,
    };
}

/**
 * Decode Relay global `id` (base64 of `TypeName:dbPk`) to numeric database id when possible.
 * @param {string|null|undefined} globalId
 * @returns {number|null}
 */
export function relayGlobalIdToDbId(globalId) {
    if (globalId == null || globalId === "") return null;
    try {
        const decoded = atob(globalId);
        const colon = decoded.indexOf(":");
        const raw = colon >= 0 ? decoded.slice(colon + 1) : decoded;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

/**
 * Relay global id, plain integer id (some responses), or numeric string → DB id.
 * @param {unknown} id
 * @returns {number|null}
 */
export function dbIdFromNodeId(id) {
    if (id == null || id === "") return null;
    if (typeof id === "number" && Number.isFinite(id)) {
        return Math.trunc(id);
    }
    if (typeof id === "string" && /^\d+$/.test(id)) {
        return parseInt(id, 10);
    }
    return relayGlobalIdToDbId(typeof id === "string" ? id : String(id));
}

/**
 * Core mutations expect Django ``PostVisibility`` **values**: ``public``, ``friends``, ``private``, ``team``.
 * Accepts legacy UI ints (1–4), uppercase names, or lowercase names.
 * @param {string|number|null|undefined} value
 * @returns {"public"|"friends"|"private"|"team"}
 */
export function normalizeCoreVisibility(value) {
    if (value === null || value === undefined || value === "") {
        return "public";
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        if (value === 2) return "friends";
        if (value === 3) return "private";
        if (value === 4) return "team";
        if (value === 5) return "nearby";
        return "public";
    }
    const str = String(value).trim();
    if (str === "") return "public";
    if (/^\d+$/.test(str)) {
        return normalizeCoreVisibility(parseInt(str, 10));
    }
    const lower = str.toLowerCase();
    if (["public", "friends", "private", "team", "nearby"].includes(lower)) {
        return lower;
    }
    const up = str.toUpperCase();
    if (up === "PUBLIC") return "public";
    if (up === "FRIENDS") return "friends";
    if (up === "PRIVATE") return "private";
    if (up === "TEAM") return "team";
    return "public";
}

/**
 * Map Core post visibility enum to legacy numeric `secure` used by the existing UI.
 * @param {string|null|undefined} visibility
 * @returns {number}
 */
export function coreVisibilityToLegacySecure(visibility) {
    const v = (visibility || "").toUpperCase();
    if (v === "PUBLIC") return 1;
    if (v === "FRIENDS") return 2;
    if (v === "PRIVATE") return 3;
    if (v === "TEAM") return 4;
    if (v === "NEARBY") return 5;
    return 1;
}
